"use client";

import { useState, useRef } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

type Filters = {
  location: string;
  locationLat: string;
  locationLng: string;
  from: string;
  fromLat: string;
  fromLng: string;
  to: string;
  toLat: string;
  toLng: string;
  type: string;
  vanSize: string;
  length: string;
  width: string;
  weight: string;
  height: string;
  serviceRadiusKm: string;
};

export default function JobFilterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const initialFilters: Filters = {
    location: "",
    locationLat: "",
    locationLng: "",
    from: "",
    fromLat: "",
    fromLng: "",
    to: "",
    toLat: "",
    toLng: "",
    type: "ANY",
    vanSize: "ANY",
    length: "",
    width: "",
    weight: "",
    height: "",
    serviceRadiusKm: "",
  };

  const [filters, setFilters] = useState<Filters>(() => {
    const entries = Object.keys(initialFilters).map((key) => [
      key,
      searchParams.get(key) ?? initialFilters[key as keyof Filters],
    ]);
    return { ...initialFilters, ...Object.fromEntries(entries) };
  });

  const locationAutoRef = useRef<google.maps.places.Autocomplete | null>(null);
  const fromAutoRef = useRef<google.maps.places.Autocomplete | null>(null);
  const toAutoRef = useRef<google.maps.places.Autocomplete | null>(null);

  const apiKey =
    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
    process.env.NEXT_PUBLIC_GOOGLE_MAPS_JAVA_API ||
    "";
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  function handleTypeChange(value: string) {
    setFilters((prev) => ({ ...prev, type: value }));
  }

  function handleVanSizeChange(value: string) {
    setFilters((prev) => ({ ...prev, vanSize: value }));
  }

  function handleServiceRadiusChange(value: string) {
    setFilters((prev) => ({ ...prev, serviceRadiusKm: value }));
  }

  function handleReset() {
    setFilters(initialFilters);
    router.push(pathname);
  }

  function handleApply(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      // Only include filters with a value and skip the 'ANY' placeholder
      if (val && val !== 'ANY') {
        params.set(key, val);
      }
    });
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname);
  }

  return (
    <form onSubmit={handleApply} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          {isLoaded && !loadError ? (
            <Autocomplete
              onLoad={(autocomplete) => (locationAutoRef.current = autocomplete)}
              onPlaceChanged={() => {
                const place = locationAutoRef.current?.getPlace();
                if (place?.geometry?.location) {
                  const formatted = place.formatted_address || "";
                  const lat = place.geometry.location.lat().toString();
                  const lng = place.geometry.location.lng().toString();
                  setFilters((prev) => ({
                    ...prev,
                    location: formatted,
                    locationLat: lat,
                    locationLng: lng,
                  }));
                }
              }}
              options={{ componentRestrictions: { country: "uk" } }}
            >
              <Input
                id="location"
                name="location"
                value={filters.location}
                onChange={handleInputChange}
              />
            </Autocomplete>
          ) : (
            <Input
              id="location"
              name="location"
              value={filters.location}
              onChange={handleInputChange}
            />
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Label htmlFor="serviceRadiusKm">Radius (km)</Label>
          <Select
            id="serviceRadiusKm"
            value={filters.serviceRadiusKm}
            onValueChange={handleServiceRadiusChange}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Radius" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="80">80</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="from">From</Label>
          {isLoaded && !loadError ? (
            <Autocomplete
              onLoad={(autocomplete) => (fromAutoRef.current = autocomplete)}
              onPlaceChanged={() => {
                const place = fromAutoRef.current?.getPlace();
                if (place?.geometry?.location) {
                  const formatted = place.formatted_address || "";
                  const lat = place.geometry.location.lat().toString();
                  const lng = place.geometry.location.lng().toString();
                  setFilters((prev) => ({
                    ...prev,
                    from: formatted,
                    fromLat: lat,
                    fromLng: lng,
                  }));
                }
              }}
              options={{ componentRestrictions: { country: "uk" } }}
            >
              <Input
                id="from"
                name="from"
                value={filters.from}
                onChange={handleInputChange}
              />
            </Autocomplete>
          ) : (
            <Input
              id="from"
              name="from"
              value={filters.from}
              onChange={handleInputChange}
            />
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="to">To</Label>
          {isLoaded && !loadError ? (
            <Autocomplete
              onLoad={(autocomplete) => (toAutoRef.current = autocomplete)}
              onPlaceChanged={() => {
                const place = toAutoRef.current?.getPlace();
                if (place?.geometry?.location) {
                  const formatted = place.formatted_address || "";
                  const lat = place.geometry.location.lat().toString();
                  const lng = place.geometry.location.lng().toString();
                  setFilters((prev) => ({
                    ...prev,
                    to: formatted,
                    toLat: lat,
                    toLng: lng,
                  }));
                }
              }}
              options={{ componentRestrictions: { country: "uk" } }}
            >
              <Input
                id="to"
                name="to"
                value={filters.to}
                onChange={handleInputChange}
              />
            </Autocomplete>
          ) : (
            <Input
              id="to"
              name="to"
              value={filters.to}
              onChange={handleInputChange}
            />
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select id="type" value={filters.type} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Any</SelectItem>
                <SelectItem value="LOGISTICS">Logistics</SelectItem>
                <SelectItem value="PALLETE">Pallet</SelectItem>
                <SelectItem value="NONSTANDARD">Non-standard</SelectItem>
                <SelectItem value="REMOVALS">Removals</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vanSize">Van Size</Label>
            <Select id="vanSize" value={filters.vanSize} onValueChange={handleVanSizeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Any</SelectItem>
                <SelectItem value="SMALL">Small</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LARGE">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-2">
            <Label htmlFor="length">Length (cm)</Label>
            <Input
              id="length"
              name="length"
              value={filters.length}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="width">Width (cm)</Label>
            <Input
              id="width"
              name="width"
              value={filters.width}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="height">Height (cm)</Label>
            <Input
              id="height"
              name="height"
              value={filters.height}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="weight">Weight (kg)</Label>
            <Input
              id="weight"
              name="weight"
              value={filters.weight}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" type="button" onClick={handleReset} className="flex-1">
            Reset Filters
          </Button>
          <Button type="submit" className="flex-1">
            Apply Filters
          </Button>
        </div>
      </form>
  );
}